import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { todayInWarsaw } from '../events/calendar';
import { ConflictNotice } from '../ui/ConflictNotice';
import { formatPln, parsePln, plnToInput } from './money';
import { NumberInput, type NumberKind } from '../ui/NumberInput';
import { formatDecimal, parseDecimal } from '../ui/numbers';
import { FieldTodoDot } from './TodoDot';
import { DraftNotice, useFormDraft } from '../nav/drafts';
import { useUnsavedGuard } from '../ui/unsaved';
import { DuplicateNameHint, type SimilarItem } from './DuplicateNameHint';
import { atDefault, bpToInput, parsePercent, suggestedRental, warrantyState, type Placement } from './whereNow';
import { DialogLayer } from '../ui/Dialog';
import { useSupplierOwners } from './SupplierOwners';

export type Kind = 'serialized' | 'bulk' | 'consumable';
export const KIND_LABELS: Record<Kind, string> = { serialized: 'Z egzemplarzami', bulk: 'Ilościowy', consumable: 'Zużywalny' };

export type CustomValue = string | number | boolean;
/** As the API sends it. A field the user may not see is simply absent. */
export type Item = {
  id: string;
  categoryId: string | null;
  name: string;
  manufacturer: string;
  model: string;
  kind: Kind;
  quantityOwned: number | null;
  minStock: number | null;
  customFields: Record<string, CustomValue>;
  purchasePriceGrosze?: number | null;
  valueGrosze?: number | null;
  rentalPriceDayGrosze: number | null;
  /** Hundredths of a percent of the purchase price (250 = 2,5 %); sensitive like the purchase price. */
  rentalFactorBp?: number | null;
  purchaseDate?: string | null;
  warrantyUntil?: string | null;
  /** F42: the purchase invoice the item (or its last purchase) came from. */
  purchaseSource?: { id: string; label: string } | null;
  defaultLocationId?: string | null;
  /** "Gdzie teraz": pieces outside the default location (for units: counted from them). */
  placements?: Placement[];
  /** "Podpowiadaj też w kategoriach" (answer 5 of remarks 4): extra categories where the item is suggested. */
  suggestCategoryIds?: string[];
  weightG: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  notes: string;
  /** Owner 2026-11-05 „Wymaga uchwytu”: in an inputlista this mic / DI needs „Statyw / uchwyt”. */
  requiresHolder?: boolean;
  ownedQuantity: number;
  totalQuantity: number;
  /** "Do uzupełnienia": completeness fields left empty, among the ones this user may see (sent by the server). */
  missingFields?: string[];
  version: number;
};
/** `autoQuoteSection` (P8): "Dodawaj automatycznie do nowego kosztorysu". */
export type Category = { id: string; name: string; parentId: string | null; sort: number; autoQuoteSection?: boolean; version: number };
export type CustomFieldDef = { id: string; key: string; label: string; type: 'text' | 'number' | 'boolean' | 'select'; options: string[] | null; sort: number; version: number };
export type Location = { id: string; name: string; sort: number; version: number };

export const CATALOG_KEY = ['catalog'] as const;
export const CATEGORIES_KEY = [...CATALOG_KEY, 'categories'] as const;
export const CUSTOM_FIELDS_KEY = [...CATALOG_KEY, 'custom-fields'] as const;
export const LOCATIONS_KEY = [...CATALOG_KEY, 'locations'] as const;

/**
 * Category names with their parents, e.g. "Mikrofony / Bezprzewodowe", in the catalog order ("Kolejność
 * kategorii", as the API sends siblings): each category followed by its subcategories, like the PDF.
 */
export function categoryLabels(categories: Category[]): { id: string; label: string }[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const children = new Map<string | null, Category[]>();
  for (const c of categories) {
    const parent = c.parentId !== null && byId.has(c.parentId) ? c.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), c]);
  }
  const out: { id: string; label: string }[] = [];
  const seen = new Set<string>();
  const visit = (parent: string | null, prefix: string) => {
    for (const c of children.get(parent) ?? []) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      const label = prefix ? `${prefix} / ${c.name}` : c.name;
      out.push({ id: c.id, label });
      visit(c.id, label);
    }
  };
  visit(null, '');
  // a cycle of parents (never expected) is not reachable from the top: list it at the end
  for (const c of categories) if (!seen.has(c.id)) out.push({ id: c.id, label: c.name });
  return out;
}

export function useCategories(enabled: boolean) {
  return useQuery({ queryKey: CATEGORIES_KEY, enabled, queryFn: () => api<{ categories: Category[] }>('/api/catalog/categories') });
}
export function useCustomFieldDefs() {
  return useQuery({ queryKey: CUSTOM_FIELDS_KEY, queryFn: () => api<{ customFields: CustomFieldDef[] }>('/api/catalog/custom-fields') });
}
export function useLocations() {
  return useQuery({ queryKey: LOCATIONS_KEY, queryFn: () => api<{ locations: Location[] }>('/api/catalog/locations') });
}
/** Contractor names to suggest in "Komu" (only for a role that may see contractors; otherwise nothing is offered). */
function useContractorNames(enabled: boolean) {
  return useQuery({
    queryKey: ['contractors', 'names'],
    enabled,
    queryFn: () => api<{ contractors: { id: string; name: string }[] }>('/api/contractors').catch(() => ({ contractors: [] })),
  });
}

type ScalarSpec = { name: keyof Item & string; label: string; type: 'money' | 'int'; size: 'num' | 'xs' | 'sm' | 'rent' };
const PURCHASE: ScalarSpec[] = [
  { name: 'purchasePriceGrosze', label: 'Cena zakupu (zł)', type: 'money', size: 'sm' },
  { name: 'valueGrosze', label: 'Wartość sprzętu (zł)', type: 'money', size: 'sm' },
];
const RENTAL: ScalarSpec = { name: 'rentalPriceDayGrosze', label: 'Docelowa cena wynajmu za dobę (zł)', type: 'money', size: 'rent' };
const PARAMS: ScalarSpec[] = [
  { name: 'weightG', label: 'Waga (g)', type: 'int', size: 'num' },
  { name: 'lengthMm', label: 'Dług. (mm)', type: 'int', size: 'num' },
  { name: 'widthMm', label: 'Szer. (mm)', type: 'int', size: 'num' },
  { name: 'heightMm', label: 'Wys. (mm)', type: 'int', size: 'num' },
];
const QTY: ScalarSpec[] = [
  { name: 'quantityOwned', label: 'Posiadana ilość', type: 'int', size: 'xs' },
  { name: 'minStock', label: 'Minimalny stan', type: 'int', size: 'xs' },
];
const SCALARS = [...QTY, ...PURCHASE, RENTAL, ...PARAMS];
/** Fields whose empty value makes the item "do uzupełnienia" (with the factor and the location, drawn apart). */
const TODO_FIELDS = new Set(['purchasePriceGrosze', 'valueGrosze', 'rentalPriceDayGrosze']);

/** A "Gdzie teraz" line being typed: the location by name (an existing one or a new one), the quantity as text. */
type PlacementRow = { key: string; quantity: string; place: Placement['place']; locationName: string; rentedTo: string; rentedUntil: string };

type Draft = {
  kind: Kind;
  name: string;
  categoryId: string;
  suggestCategoryIds: string[];
  manufacturer: string;
  model: string;
  notes: string;
  requiresHolder: boolean;
  rentalFactor: string;
  purchaseDate: string;
  warrantyUntil: string;
  defaultLocationId: string;
  placements: PlacementRow[];
  scalars: Record<string, string>;
  custom: Record<string, string | boolean>;
};

let rowSeq = 0;
const rowKey = () => `p${++rowSeq}`;

function scalarText(spec: ScalarSpec, item?: Item): string {
  const value = item?.[spec.name] as number | null | undefined;
  if (value === null || value === undefined) return '';
  return spec.type === 'money' ? plnToInput(value) : String(value);
}

function customDraft(defs: CustomFieldDef[], item?: Item): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const def of defs) {
    const value = item?.customFields?.[def.key];
    out[def.key] = def.type === 'boolean' ? value === true : value === undefined ? '' : def.type === 'number' && typeof value === 'number' ? formatDecimal(value) : String(value);
  }
  return out;
}

const placementRows = (item: Item | undefined, names: ReadonlyMap<string, string>): PlacementRow[] =>
  item?.kind === 'serialized'
    ? []
    : (item?.placements ?? []).map((l, i) => ({
        key: `s${i}`, // stable while the stored lines are shown untouched
        quantity: String(l.quantity),
        place: l.place,
        locationName: l.locationId ? (names.get(l.locationId) ?? '') : '',
        rentedTo: l.rentedTo,
        rentedUntil: l.rentedUntil ?? '',
      }));

const draftOf = (defs: CustomFieldDef[], names: ReadonlyMap<string, string>, item?: Item, firstLocationId = ''): Draft => ({
  kind: item?.kind ?? 'serialized',
  name: item?.name ?? '',
  categoryId: item?.categoryId ?? '',
  suggestCategoryIds: item?.suggestCategoryIds ?? [],
  manufacturer: item?.manufacturer ?? '',
  model: item?.model ?? '',
  notes: item?.notes ?? '',
  requiresHolder: item?.requiresHolder ?? false,
  rentalFactor: item?.rentalFactorBp == null ? '' : bpToInput(item.rentalFactorBp),
  purchaseDate: item?.purchaseDate ?? '',
  warrantyUntil: item?.warrantyUntil ?? '',
  defaultLocationId: item ? (item.defaultLocationId ?? '') : firstLocationId,
  placements: placementRows(item, names),
  scalars: Object.fromEntries(SCALARS.map((s) => [s.name, scalarText(s, item)])),
  custom: customDraft(defs, item),
});

/** Custom values as the API wants them: only keys of current definitions, empty ones left out. Null where a number is not valid. */
function customValues(defs: CustomFieldDef[], draft: Record<string, string | boolean>): Record<string, CustomValue> | null {
  const out: Record<string, CustomValue> = {};
  for (const def of defs) {
    const raw = draft[def.key];
    if (def.type === 'boolean') {
      if (raw === true) out[def.key] = true;
    } else if (typeof raw === 'string' && raw.trim() !== '') {
      if (def.type === 'number') {
        const n = parseDecimal(raw, { maxFractionDigits: 6, negative: true });
        if (n === null) return null;
        out[def.key] = n;
      } else out[def.key] = raw.trim();
    }
  }
  return out;
}

/** The lines as the API wants them; the comparable form (by location name) decides whether they changed. */
function placementsOut(rows: PlacementRow[], locations: Location[]): { body: Record<string, unknown>[]; key: string; error: string | null; sum: number } {
  const byName = new Map(locations.map((l) => [l.name.toLocaleLowerCase('pl'), l.id]));
  const body: Record<string, unknown>[] = [];
  const comparable: unknown[] = [];
  let sum = 0;
  let error: string | null = null;
  for (const r of rows) {
    const q = r.quantity.trim();
    if (!/^\d{1,9}$/.test(q) || Number(q) < 1) error = 'Ilość w „Gdzie teraz”: liczba całkowita od 1.';
    const quantity = Number(q);
    sum += quantity || 0;
    if (r.place === 'location') {
      const name = r.locationName.trim();
      if (name === '') error = 'Wpisz lokalizację albo usuń wiersz.';
      const id = byName.get(name.toLocaleLowerCase('pl'));
      body.push({ place: 'location', quantity, ...(id ? { locationId: id } : { locationName: name }) });
      comparable.push(['location', name.toLocaleLowerCase('pl'), quantity]);
    } else {
      body.push({ place: 'rented', quantity, rentedTo: r.rentedTo.trim(), rentedUntil: r.rentedUntil || null });
      comparable.push(['rented', r.rentedTo.trim(), r.rentedUntil || null, quantity]);
    }
  }
  return { body, key: JSON.stringify(comparable), error, sum };
}

function FieldError({ error, path }: { error: unknown; path: string }) {
  if (!(error instanceof ApiError)) return null;
  const message = fieldIssues(error).find((i) => i.path === path)?.message;
  return message ? <span className="field-error">{message}</span> : null;
}

/** A 409 without the current record is not a stale form but "busy, try again" (e.g. a kind change during a reservation). */
const staleForm = (error: unknown) => error instanceof ApiError && error.status === 409 && error.current != null;

function GeneralError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || staleForm(error)) return null;
  if (error.error === 'validation' && fieldIssues(error).length > 0) return null;
  return (
    <p className="alert" role="alert">
      {error.message}
    </p>
  );
}

/** One group of the form: a small heading, compact fields inside, clear space around (rule 10 of docs/wyglad.md). */
function Group({ title, wide = false, children }: { title: string; wide?: boolean; children: ReactNode }) {
  return (
    <fieldset className={`ig${wide ? ' ig-wide' : ''}`}>
      <legend>{title}</legend>
      {children}
    </fieldset>
  );
}

const unitsWord = (n: number) => (n === 1 ? 'egzemplarz' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'egzemplarze' : 'egzemplarzy');

/**
 * What saving a new kind will do (owner 2026-10-19 23:50: no "czy na pewno", a short line under the field instead).
 * The server does the same (catalog `planKindChange`); null when the kind is unchanged.
 */
export function kindPreview(item: Item, to: Kind): string | null {
  if (to === item.kind) return null;
  if (to === 'serialized') {
    const n = item.quantityOwned ?? 0;
    if (n === 0) return 'Pozycja będzie miała egzemplarze; teraz żadnego (ilość 0). Dodasz je w szczegółach.';
    return `${n === 1 ? 'Powstanie' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'Powstaną' : 'Powstanie'} ${n} ${unitsWord(n)} bez numerów seryjnych (oznaczonych 1–${n}); numery wpiszesz potem w szczegółach.`;
  }
  if (item.kind === 'serialized') {
    return `Ilość będzie ${item.ownedQuantity} (dostępne egzemplarze); egzemplarze z numerami seryjnymi zostaną zarchiwizowane (zostają w historii).`;
  }
  return item.kind === 'consumable' && item.minStock != null ? 'Minimalny stan zostanie usunięty.' : 'Zmieni się tylko rodzaj.';
}

/**
 * "Podpowiadaj też w kategoriach" (answer 5 of remarks 4): the chosen categories as small chips with ×, and a select to
 * add one more. Only a hint for the quick pickers of quotes and packlists: nothing is blocked anywhere.
 */
function SuggestCategories({ labels, value, onChange, issue }: { labels: { id: string; label: string }[]; value: string[]; onChange: (ids: string[]) => void; issue: ReactNode }) {
  const name = new Map(labels.map((c) => [c.id, c.label]));
  const shown = value.filter((id) => name.has(id));
  const free = labels.filter((c) => !value.includes(c.id));
  return (
    <div className="field w-lg suggest-cats" role="group" aria-label="Podpowiadaj też w kategoriach">
      <span>Podpowiadaj też w kategoriach</span>
      <div className="suggest-cats-row">
        {shown.map((id) => (
          <span key={id} className="chip suggest-chip">
            {name.get(id)}
            <button type="button" className="chip-x" aria-label={`Usuń z podpowiedzi: ${name.get(id)}`} onClick={() => onChange(value.filter((x) => x !== id))}>
              ×
            </button>
          </span>
        ))}
        {free.length > 0 && (
          <select
            aria-label="Dodaj kategorię podpowiedzi"
            value=""
            onChange={(e) => {
              if (e.target.value) onChange([...value, e.target.value]);
            }}
          >
            <option value="">{shown.length ? '+ Dodaj' : '+ Dodaj kategorię'}</option>
            {free.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}
      </div>
      <span className="hint">Np. paszcza z systemu podpowiadana też w sekcji „Monitory”. Niczego nie blokuje.</span>
      {issue}
    </div>
  );
}

/** "Odrzucić zmiany?": asked in the app (not the browser) when the form has unsaved changes. */
function DiscardDialog({ onDiscard, onBack }: { onDiscard: () => void; onBack: () => void }) {
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="discard-title">
        <DialogLayer onClose={onBack} />
        <h2 id="discard-title">Odrzucić zmiany?</h2>
        <p>Wpisane zmiany nie zostaną zapisane.</p>
        <div className="actions">
          <button className="btn primary" type="button" onClick={onDiscard} autoFocus>
            Odrzuć zmiany
          </button>
          <button className="btn" type="button" onClick={onBack}>
            Wróć do edycji
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Add (no `item`) or edit. `visible(field)`: the user may see and set that field (`hiddenFields` of the session,
 * rule 7a). A hidden field is neither shown nor sent. On edit only changed fields go out. While it is open the page
 * shows only "Zapisz zmiany" and "Odrzuć zmiany" (owner remark 3/2).
 */
export function ItemForm({
  item,
  copyFrom,
  visible,
  canPickCategory,
  canSeeContractors = false,
  canSeeSupplierOwners = false,
  onDone,
  onSaved,
  onExisting,
}: {
  item?: Item;
  /**
   * "Duplikuj" (owner 2026-10-19 23:16): a new item filled from this one (what the user may see of it), named
   * "<name> (kopia)" with the name selected. Units, "Gdzie teraz", comparison prices, documents and history are not
   * copied. Nothing is saved before "Zapisz zmiany".
   */
  copyFrom?: Item;
  visible: (field: string) => boolean;
  canPickCategory: boolean;
  canSeeContractors?: boolean;
  /** „Mają to” (read-only, in „Ceny”) for a stored item: `seesSupplierOwners` — rule 7a (owner 2026-10-24 10:55). */
  canSeeSupplierOwners?: boolean;
  onDone: () => void;
  /** After „Zapisz zmiany” saved (not a save from „Masz niezapisane zmiany” on the way out), after `onDone`. */
  onSaved?: (() => void) | undefined;
  /**
   * P14: the name is already in the catalog and the person chose "Otwórz" ('open') or "Dodaj sztuki do istniejącej"
   * ('add'). Without it the warning offers only "Utwórz mimo to".
   */
  onExisting?: (item: SimilarItem, how: 'open' | 'add') => void;
}) {
  const queryClient = useQueryClient();
  const ids = useId();
  const defs = useCustomFieldDefs().data?.customFields ?? [];
  const categories = useCategories(canPickCategory).data?.categories ?? [];
  const locationsQuery = useLocations();
  const locations = locationsQuery.data?.locations ?? [];
  const names = new Map(locations.map((l) => [l.id, l.name]));
  const contractors = useContractorNames(canSeeContractors && visible('placements')).data?.contractors ?? [];
  const owners = useSupplierOwners(item?.id, canSeeSupplierOwners);
  const [base, setBase] = useState(item);
  const copyDraft = (defs: CustomFieldDef[], from: Item, first = ''): Draft => ({ ...draftOf(defs, names, from, first), name: `${from.name} (kopia)`, placements: [] });
  // P13: what was typed waits for the way back (a copy lives only while the screen is open: no draft)
  const kept = useFormDraft<{ draft: Draft; touched: { custom: boolean; where: boolean } }>(copyFrom ? null : `catalog.item:${item?.id ?? 'new'}`, base?.version ?? 0);
  const [draft, setDraft] = useState(() => kept.initial?.draft ?? (copyFrom ? copyDraft([], copyFrom) : draftOf([], names, item)));
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!copyFrom) return;
    nameRef.current?.focus();
    nameRef.current?.select(); // the person types the new name right away
    // only when the copy opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // definitions and locations arrive after the first render; until the person touches them, show the stored values
  const [touched, setTouched] = useState<{ custom: boolean; where: boolean }>(() => kept.initial?.touched ?? { custom: false, where: false });
  kept.track({ draft, touched });
  /** Saved or discarded: the draft is no longer wanted. */
  const finish = () => {
    kept.clear();
    onDone();
  };
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const firstLocation = locations[0]?.id ?? '';
  const original = draftOf(defs, names, base, firstLocation);
  // what the form starts from: the stored item, the item being copied, or nothing (a new item)
  const seed = copyFrom && !base ? copyDraft(defs, copyFrom, firstLocation) : original;
  const custom = touched.custom ? draft.custom : seed.custom;
  const where = touched.where ? { defaultLocationId: draft.defaultLocationId, placements: draft.placements } : { defaultLocationId: seed.defaultLocationId, placements: seed.placements };
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const setScalar = (name: string, value: string) => set({ scalars: { ...draft.scalars, [name]: value } });
  const setWhere = (patch: Partial<Pick<Draft, 'defaultLocationId' | 'placements'>>) => {
    setDraft({ ...draft, ...where, ...patch });
    setTouched({ ...touched, where: true });
  };

  const kind = base?.kind ?? draft.kind;
  /** The kind is being changed (owner 23:50): the server then decides the quantity, breakdown and units itself. */
  const kindChanged = !!base && draft.kind !== base.kind;
  const showQty = (name: string) => !kindChanged && (name === 'quantityOwned' ? kind !== 'serialized' : kind === 'consumable') && visible(name);

  // the quantity the breakdown must fit: as typed, or the stored one when the field is hidden from this role
  const ownedNow = visible('quantityOwned') ? Number(draft.scalars.quantityOwned) || 0 : (base?.totalQuantity ?? 0);

  function buildFields(): { fields: Record<string, unknown>; errors: Record<string, string> } {
    const problems: Record<string, string> = {};
    const fields: Record<string, unknown> = {};
    const wanted = (field: string, changed: boolean) => visible(field) && (!base || changed);
    if (!base || kindChanged) fields.kind = draft.kind; // required when adding
    if (wanted('name', draft.name !== original.name)) fields.name = draft.name;
    if (canPickCategory && wanted('categoryId', draft.categoryId !== original.categoryId)) fields.categoryId = draft.categoryId || null;
    if (canPickCategory && wanted('suggestCategoryIds', draft.suggestCategoryIds.join() !== original.suggestCategoryIds.join())) fields.suggestCategoryIds = draft.suggestCategoryIds;
    for (const key of ['manufacturer', 'model', 'notes'] as const) if (wanted(key, draft[key] !== original[key])) fields[key] = draft[key];
    // a new item: only when ticked (the default is „no”)
    if (visible('requiresHolder') && (base ? draft.requiresHolder !== original.requiresHolder : draft.requiresHolder)) fields.requiresHolder = draft.requiresHolder;
    for (const key of ['purchaseDate', 'warrantyUntil'] as const) {
      if (wanted(key, draft[key] !== original[key]) && (base || draft[key])) fields[key] = draft[key] || null;
    }
    if (wanted('rentalFactorBp', draft.rentalFactor.trim() !== original.rentalFactor)) {
      const text = draft.rentalFactor.trim();
      if (text === '') {
        if (base) fields.rentalFactorBp = null;
      } else {
        try {
          fields.rentalFactorBp = parsePercent(text);
        } catch (e) {
          problems.rentalFactorBp = (e as Error).message;
        }
      }
    }
    for (const spec of SCALARS) {
      const isQty = spec.name === 'quantityOwned' || spec.name === 'minStock';
      if (isQty && !showQty(spec.name)) continue;
      const text = draft.scalars[spec.name]!.trim();
      if (!wanted(spec.name, text !== original.scalars[spec.name])) continue;
      if (text === '') {
        if (spec.name === 'quantityOwned') problems[spec.name] = 'Podaj posiadaną ilość.';
        else if (base) fields[spec.name] = null;
        continue;
      }
      try {
        if (spec.type === 'money') fields[spec.name] = parsePln(text);
        else if (/^\d{1,9}$/.test(text)) fields[spec.name] = Number(text);
        else throw new Error('Podaj liczbę całkowitą, np. 12.');
      } catch (e) {
        problems[spec.name] = (e as Error).message;
      }
    }
    if (!base && draft.kind !== 'serialized' && draft.scalars.quantityOwned!.trim() === '') problems.quantityOwned = 'Podaj posiadaną ilość.';
    if (wanted('defaultLocationId', where.defaultLocationId !== original.defaultLocationId) && where.defaultLocationId) fields.defaultLocationId = where.defaultLocationId;
    if (visible('placements') && kind !== 'serialized' && !kindChanged) {
      const now = placementsOut(where.placements, locations);
      const before = placementsOut(original.placements, locations);
      const owned = ownedNow;
      const quantityChanged = draft.scalars.quantityOwned!.trim() !== original.scalars.quantityOwned;
      // a breakdown left above the stock (consumables used up) is checked only when quantity or breakdown change
      if (now.error) problems.placements = now.error;
      else if ((quantityChanged || now.key !== before.key || !base) && now.sum > owned) problems.placements = `Poza domyślną lokalizacją jest ${now.sum} szt., a pozycja ma ${owned} szt.`;
      else if (now.key !== before.key) fields.placements = now.body;
    }
    if (visible('customFields')) {
      const now = customValues(defs, custom);
      const before = customValues(defs, original.custom);
      if (now === null) problems.customFields = 'Pole liczbowe zawiera niepoprawną liczbę.';
      else if (!base || JSON.stringify(Object.entries(now).sort()) !== JSON.stringify(Object.entries(before ?? {}).sort())) fields.customFields = now;
    }
    return { fields, errors: problems };
  }

  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      base ? api<Item>(`/api/catalog/items/${base.id}`, { method: 'PATCH', body: { version: base.version, ...fields } }) : api<Item>('/api/catalog/items', { method: 'POST', body: fields }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
      finish();
      if (submitted.current) onSaved?.();
    },
  });
  /** The save in flight came from „Zapisz zmiany” (the form's submit). */
  const submitted = useRef(false);
  const conflict = staleForm(save.error);

  async function reload() {
    const current = (save.error as ApiError).current as Item | undefined;
    const fresh = current ?? (await api<Item>(`/api/catalog/items/${base!.id}`));
    setBase(fresh);
    setDraft(draftOf(defs, names, fresh));
    setTouched({ custom: false, where: false });
    save.reset();
    await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const built = buildFields();
    setErrors(built.errors);
    if (Object.keys(built.errors).length > 0) return;
    submitted.current = true;
    save.mutate(built.fields, { onSettled: () => void (submitted.current = false) });
  }

  /** Anything typed that would be saved (or is wrong)? Then "Odrzuć zmiany" asks first. */
  function dirty(): boolean {
    const built = buildFields();
    if (Object.keys(built.errors).length > 0) return true;
    const sent = Object.keys(built.fields).filter((k) => k !== 'kind');
    if (base) return sent.length > 0;
    const blank = draftOf(defs, names, undefined, firstLocation);
    return JSON.stringify({ ...draft, ...where, custom, placements: where.placements.length }) !== JSON.stringify({ ...blank, custom: blank.custom, placements: 0 });
  }
  const discard = () => (dirty() ? setConfirming(true) : finish());
  // owner 2026-10-23 13:34: leaving the screen with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: dirty(),
    save: async () => {
      const built = buildFields();
      setErrors(built.errors);
      if (Object.keys(built.errors).length > 0) return false;
      await save.mutateAsync(built.fields);
      return true;
    },
    discard: () => kept.clear(),
  });

  const fieldIssue = (field: string) => (
    <>
      {errors[field] && <span className="field-error">{errors[field]}</span>}
      <FieldError error={save.error} path={field} />
    </>
  );
  const text = (field: string, label: string, value: string, onChange: (v: string) => void, props: { required?: boolean; kind?: NumberKind; size?: 'num' | 'xs' | 'sm' | 'md' | 'lg' | 'half' | 'rent'; inputRef?: React.Ref<HTMLInputElement> } = {}) =>
    visible(field) && (
      <label className={`field w-${props.size ?? 'md'}`}>
        <span>
          {label}
          <FieldTodoDot empty={TODO_FIELDS.has(field) && value.trim() === ''} />
        </span>
        {props.kind ? <NumberInput kind={props.kind} value={value} onValueChange={onChange} required={props.required} /> : <input ref={props.inputRef} value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" required={props.required} />}
        {fieldIssue(field)}
      </label>
    );
  const scalar = (s: ScalarSpec) => <div key={s.name} className="contents">{text(s.name, s.label, draft.scalars[s.name]!, (v) => setScalar(s.name, v), { kind: s.type === 'money' ? 'money' : 'integer', size: s.size })}</div>;
  const date = (field: 'purchaseDate' | 'warrantyUntil', label: string, extra?: ReactNode) =>
    visible(field) && (
      <label className="field w-sm">
        <span>{label}</span>
        <input type="date" value={draft[field]} onChange={(e) => set({ [field]: e.target.value })} />
        {extra}
        {fieldIssue(field)}
      </label>
    );

  // the suggestion needs both the purchase price and the factor (both sensitive): shown only to who sees them
  const canSuggest = visible('purchasePriceGrosze') && visible('rentalFactorBp');
  let suggestion: number | null = null;
  try {
    const purchase = draft.scalars.purchasePriceGrosze!.trim();
    const factor = draft.rentalFactor.trim();
    suggestion = canSuggest && purchase && factor ? suggestedRental(parsePln(purchase), parsePercent(factor)) : null;
  } catch {
    suggestion = null; // a value still being typed
  }
  const warranty = warrantyState(draft.warrantyUntil || null, todayInWarsaw());
  const owned = ownedNow;
  const placedSum = where.placements.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const defaultName = names.get(where.defaultLocationId) ?? 'domyślnej lokalizacji';
  const setRow = (key: string, patch: Partial<PlacementRow>) => setWhere({ placements: where.placements.map((r) => (r.key === key ? { ...r, ...patch } : r)) });
  const hasPrices = PURCHASE.some((s) => visible(s.name)) || visible('rentalFactorBp') || visible(RENTAL.name);
  const hasParams = PARAMS.some((s) => visible(s.name)) || (visible('customFields') && defs.length > 0);
  const hasPurchase = visible('purchaseDate') || visible('warrantyUntil');
  const hasWhere = QTY.some((s) => showQty(s.name)) || visible('defaultLocationId') || visible('placements');

  return (
    <form ref={guardRef} className={`item-form${base ? '' : ' panel'}`} aria-label={base ? `Edycja: ${base.name}` : 'Nowa pozycja'} data-feature-id="catalog.edit" onSubmit={submit}>
      {!base && <h2>{copyFrom ? `Nowa pozycja — kopia „${copyFrom.name}”` : 'Nowa pozycja'}</h2>}
      <DraftNotice
        keeper={kept}
        onRestore={(v) => {
          setDraft(v.draft);
          setTouched(v.touched);
        }}
      />
      {conflict && <ConflictNotice onReload={() => void reload()} />}
      <GeneralError error={save.error} />
      <div className="ig-grid">
        <Group title="Dane produktu">
          <div className="ig-row">{text('name', 'Nazwa', draft.name, (v) => set({ name: v }), { required: true, size: 'lg', inputRef: nameRef })}</div>
          {/* P14: the same name already in the catalog (while adding, copying or renaming; never the item itself) */}
          {visible('name') && (!base || draft.name.trim() !== base.name) && (
            <DuplicateNameHint
              name={draft.name}
              excludeId={base?.id}
              dismissLabel={base ? 'Zostaw mimo to' : 'Utwórz mimo to'}
              actions={(found) =>
                onExisting
                  ? [
                      { label: 'Otwórz', onClick: () => onExisting(found, 'open') },
                      ...(base ? [] : [{ label: 'Dodaj sztuki do istniejącej', onClick: () => onExisting(found, 'add') }]),
                    ]
                  : []
              }
            />
          )}
          <div className="ig-row">
            {text('manufacturer', 'Producent', draft.manufacturer, (v) => set({ manufacturer: v }), { size: 'half' })}
            {text('model', 'Model', draft.model, (v) => set({ model: v }), { size: 'half' })}
          </div>
          <div className="ig-row">
            {(!base || visible('kind')) && (
              <label className="field w-half">
                <span>Rodzaj</span>
                <select value={draft.kind} onChange={(e) => set({ kind: e.target.value as Kind })}>
                  {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
                {base && kindChanged && (
                  <span className="hint kind-preview" data-testid="kind-preview" aria-live="polite">
                    {kindPreview(base, draft.kind)}
                  </span>
                )}
                {fieldIssue('kind')}
              </label>
            )}
            {canPickCategory && visible('categoryId') && (
              <label className="field w-half">
                <span>Kategoria</span>
                <select value={draft.categoryId} onChange={(e) => set({ categoryId: e.target.value })}>
                  <option value="">Bez kategorii</option>
                  {categoryLabels(categories).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <FieldError error={save.error} path="categoryId" />
              </label>
            )}
          </div>
          {canPickCategory && visible('suggestCategoryIds') && (
            <SuggestCategories
              labels={categoryLabels(categories).filter((c) => c.id !== draft.categoryId)}
              value={draft.suggestCategoryIds.filter((id) => id !== draft.categoryId)}
              onChange={(ids) => set({ suggestCategoryIds: ids })}
              issue={fieldIssue('suggestCategoryIds')}
            />
          )}
          {visible('notes') && (
            <label className="field w-lg">
              <span>Opis i uwagi</span>
              <textarea rows={2} value={draft.notes} onChange={(e) => set({ notes: e.target.value })} />
              <FieldError error={save.error} path="notes" />
            </label>
          )}
          {visible('requiresHolder') && (
            <label className="check w-lg">
              <input type="checkbox" checked={draft.requiresHolder} onChange={(e) => set({ requiresHolder: e.target.checked })} />
              <span>
                Wymaga uchwytu <span className="muted small">— w inputliście ten mikrofon / DI musi mieć „Statyw / uchwyt”</span>
              </span>
            </label>
          )}
        </Group>

        {(hasPrices || owners) && (
          <Group title="Ceny">
            <div className="ig-row">{PURCHASE.map(scalar)}</div>
            {visible('rentalFactorBp') && (
              <div className="ig-row ig-suggest">
                <label className="field">
                  <span>
                    Współczynnik wynajmu (% ceny zakupu)
                    <FieldTodoDot empty={draft.rentalFactor.trim() === ''} />
                  </span>
                  <NumberInput value={draft.rentalFactor} onValueChange={(v) => set({ rentalFactor: v })} placeholder="np. 2,5" />
                  {fieldIssue('rentalFactorBp')}
                </label>
                {canSuggest && (
                  <div className="suggested" aria-live="polite">
                    <span className="suggested-label">Sugerowana cena za dobę</span>
                    <span className="suggested-value" data-testid="suggested-rental">
                      {suggestion === null ? '—' : formatPln(suggestion)}
                    </span>
                    {suggestion !== null && visible(RENTAL.name) && (
                      <button className="btn small" type="button" onClick={() => setScalar(RENTAL.name, plnToInput(suggestion))}>
                        Użyj sugerowanej
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="ig-row">{scalar(RENTAL)}</div>
            {owners && (
              <div className="ig-row" data-feature-id="subrentals.catalog_match">
                <div className="field">
                  <span>Ceny u dostawców (za dobę)</span>
                  {owners}
                </div>
              </div>
            )}
          </Group>
        )}

        {hasParams && (
          <Group title="Parametry">
            <div className="ig-row">{PARAMS.map(scalar)}</div>
            {visible('customFields') && defs.length > 0 && (
              <div className="ig-row">
                {defs.map((def) => {
                  const value = custom[def.key];
                  const setCustom = (v: string | boolean) => {
                    setDraft({ ...draft, custom: { ...custom, [def.key]: v } });
                    setTouched({ ...touched, custom: true });
                  };
                  if (def.type === 'boolean')
                    return (
                      <label key={def.id} className="check">
                        <input type="checkbox" checked={value === true} onChange={(e) => setCustom(e.target.checked)} />
                        <span>{def.label}</span>
                      </label>
                    );
                  return (
                    <label key={def.id} className={`field ${def.type === 'number' ? 'w-xs' : 'w-md'}`}>
                      <span>{def.label}</span>
                      {def.type === 'select' ? (
                        <select value={String(value ?? '')} onChange={(e) => setCustom(e.target.value)}>
                          <option value="">—</option>
                          {(def.options ?? []).map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : (
                        def.type === 'number' ? <NumberInput value={String(value ?? '')} onValueChange={setCustom} /> : <input value={String(value ?? '')} onChange={(e) => setCustom(e.target.value)} autoComplete="off" />
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            {errors.customFields && <span className="field-error">{errors.customFields}</span>}
          </Group>
        )}

        {hasPurchase && (
          <Group title="Zakup i gwarancja">
            <div className="ig-row">
              {date('purchaseDate', 'Data zakupu')}
              {date('warrantyUntil', 'Gwarancja do', warranty && <span className={`chip ${warranty.kind === 'expired' ? 'sensitive' : 'accent'}`}>{warranty.label}</span>)}
            </div>
          </Group>
        )}

        {hasWhere && (
          <Group title="Ilość i lokalizacja" wide>
            <div className="ig-row">
              {QTY.map((s) => showQty(s.name) && scalar(s))}
              {visible('defaultLocationId') && (
                <label className="field w-md">
                  <span>
                    Domyślna lokalizacja
                    <FieldTodoDot empty={where.defaultLocationId === ''} />
                  </span>
                  <select value={where.defaultLocationId} onChange={(e) => setWhere({ defaultLocationId: e.target.value })}>
                    {where.defaultLocationId === '' && <option value="">—</option>}
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  <FieldError error={save.error} path="defaultLocationId" />
                </label>
              )}
            </div>
            {visible('placements') && kind === 'serialized' && !kindChanged && <p className="hint">Gdzie jest sprzęt z egzemplarzami, ustawiasz przy każdym egzemplarzu (w szczegółach pozycji).</p>}
            {visible('placements') && kind !== 'serialized' && !kindChanged && (
              <div className="where" role="group" aria-label="Gdzie teraz">
                <div className="where-head">
                  <span className="where-title">Gdzie teraz</span>
                  <span className={`where-rest${placedSum > owned ? ' over' : ''}`} data-testid="where-rest">
                    {placedSum > owned ? `Rozbicie przekracza ilość o ${placedSum - owned} szt.` : `${atDefault(owned, where.placements.map((r) => ({ place: r.place, quantity: Number(r.quantity) || 0, locationId: null, rentedTo: '', rentedUntil: null })))} szt. w: ${defaultName}`}
                  </span>
                </div>
                {where.placements.map((r, i) => (
                  <div key={r.key} className="where-row" role="group" aria-label={`Gdzie teraz, wiersz ${i + 1}`}>
                    <label className="field w-xs">
                      <span>Ilość</span>
                      <NumberInput kind="integer" value={r.quantity} onValueChange={(v) => setRow(r.key, { quantity: v })} />
                    </label>
                    <label className="field w-sm">
                      <span>Gdzie</span>
                      <select value={r.place} onChange={(e) => setRow(r.key, { place: e.target.value as Placement['place'] })}>
                        <option value="location">Inna lokalizacja</option>
                        <option value="rented">Wypożyczone</option>
                      </select>
                    </label>
                    {r.place === 'location' ? (
                      <label className="field w-md">
                        <span>Lokalizacja</span>
                        <input value={r.locationName} onChange={(e) => setRow(r.key, { locationName: e.target.value })} list={`${ids}-locations`} autoComplete="off" placeholder="wybierz albo wpisz" />
                      </label>
                    ) : (
                      <>
                        <label className="field w-md">
                          <span>Komu (opcjonalnie)</span>
                          <input value={r.rentedTo} onChange={(e) => setRow(r.key, { rentedTo: e.target.value })} list={`${ids}-contractors`} autoComplete="off" />
                        </label>
                        <label className="field w-sm">
                          <span>Do kiedy (opcjonalnie)</span>
                          <input type="date" value={r.rentedUntil} onChange={(e) => setRow(r.key, { rentedUntil: e.target.value })} />
                        </label>
                      </>
                    )}
                    <button className="btn ghost small where-remove" type="button" aria-label={`Usuń wiersz ${i + 1} z „Gdzie teraz”`} onClick={() => setWhere({ placements: where.placements.filter((x) => x.key !== r.key) })}>
                      Usuń
                    </button>
                  </div>
                ))}
                <button
                  className="btn ghost small"
                  type="button"
                  onClick={() => setWhere({ placements: [...where.placements, { key: rowKey(), quantity: '1', place: 'location', locationName: '', rentedTo: '', rentedUntil: '' }] })}
                >
                  + Inne miejsce albo wypożyczenie
                </button>
                {fieldIssue('placements')}
                <datalist id={`${ids}-locations`}>
                  {locations.map((l) => (
                    <option key={l.id} value={l.name} />
                  ))}
                </datalist>
                <datalist id={`${ids}-contractors`}>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
              </div>
            )}
          </Group>
        )}
      </div>
      <div className="form-bar">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </button>
        <button className="btn" type="button" onClick={discard}>
          Odrzuć zmiany
        </button>
      </div>
      {confirming && <DiscardDialog onDiscard={finish} onBack={() => setConfirming(false)} />}
    </form>
  );
}
